import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FiX, FiSearch, FiCheck, FiAlertCircle, FiLoader } from 'react-icons/fi';
import { useToast } from '../../components/ui/toast';
import SublyApi from '../../helpers/Api';

const DiscoveryModal = ({ isOpen, onClose, onComplete, token, existingKeys }) => {
	const [isDiscovering, setIsDiscovering] = useState(false);
	const [discoveredKeys, setDiscoveredKeys] = useState([]);
	const [selectedKeys, setSelectedKeys] = useState([]);
	const [isImporting, setIsImporting] = useState(false);
	const [error, setError] = useState(null);
	const [importResults, setImportResults] = useState(null);
	const hasDiscovered = useRef(false);

	const { addToast } = useToast();

	// Auto-discover when modal opens
	useEffect(() => {
		if (isOpen && !hasDiscovered.current) {
			hasDiscovered.current = true;
			handleDiscover();
		}

		// Reset ref when modal closes
		if (!isOpen) {
			hasDiscovered.current = false;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	// Discover new lookup keys from Stripe
	const handleDiscover = async () => {
		setIsDiscovering(true);
		setError(null);
		setDiscoveredKeys([]);
		setSelectedKeys([]);
		setImportResults(null);

		try {
			const header = { Authorization: `Bearer ${token}` };
			const response = await SublyApi.request(
				'api/admin/subscription-lookup-keys/discover',
				{},
				'get',
				header
			);

			// Backend already filters out existing keys, so use the response directly
			const newKeys = response.discoveredKeys || [];

			setDiscoveredKeys(newKeys);

			if (newKeys.length === 0) {
				addToast({
					title: 'Discovery Complete',
					description: 'No new lookup keys found. All Stripe keys are already in the system.',
					type: 'info',
				});
			} else {
				addToast({
					title: 'Discovery Complete',
					description: `Found ${newKeys.length} new lookup key${newKeys.length !== 1 ? 's' : ''}`,
					type: 'success',
				});
			}
		} catch (err) {
			console.error('Error discovering lookup keys:', err);
			setError('Failed to discover lookup keys from Stripe');
			addToast({
				title: 'Discovery Failed',
				description: 'Failed to discover lookup keys from Stripe',
				type: 'error',
			});
		} finally {
			setIsDiscovering(false);
		}
	};

	// Toggle key selection
	const handleToggleKey = (lookupKey) => {
		setSelectedKeys(prev => {
			if (prev.includes(lookupKey)) {
				return prev.filter(k => k !== lookupKey);
			} else {
				return [...prev, lookupKey];
			}
		});
	};

	// Select all keys
	const handleSelectAll = () => {
		if (selectedKeys.length === discoveredKeys.length) {
			setSelectedKeys([]);
		} else {
			setSelectedKeys(discoveredKeys.map(k => k.lookupKey));
		}
	};

	// Import selected keys
	const handleImport = async () => {
		if (selectedKeys.length === 0) {
			addToast({
				title: 'No Keys Selected',
				description: 'Please select at least one lookup key to import',
				type: 'warning',
			});
			return;
		}

		setIsImporting(true);
		setError(null);

		try {
			// Convert selected key strings to full key objects for the API
			const keysToImport = discoveredKeys
				.filter(key => selectedKeys.includes(key.lookupKey))
				.map(key => ({
					lookup_key: key.lookupKey,
					product_name: key.productName,
					formattedName: key.formattedName,
					name: key.formattedName || key.productName
				}));

			const header = { Authorization: `Bearer ${token}` };
			const response = await SublyApi.request(
				'api/admin/subscription-lookup-keys/sync',
				{ lookupKeys: keysToImport },
				'post',
				header
			);

			setImportResults(response);

			addToast({
				title: 'Import Successful',
				description: `Successfully imported ${response.summary.successful} lookup key${response.summary.successful !== 1 ? 's' : ''}`,
				type: 'success',
			});

			// Close modal and refresh parent
			setTimeout(() => {
				onComplete();
			}, 1500);
		} catch (err) {
			console.error('Error importing lookup keys:', err);
			setError('Failed to import lookup keys');
			addToast({
				title: 'Import Failed',
				description: 'Failed to import lookup keys',
				type: 'error',
			});
		} finally {
			setIsImporting(false);
		}
	};

	if (!isOpen) return null;

	return (
		<>
			{/* Backdrop */}
			<div
				className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
				onClick={onClose}
				aria-hidden="true"
			/>

			{/* Modal */}
			<div className="fixed inset-0 z-50 overflow-y-auto">
				<div className="flex min-h-full items-center justify-center p-4">
					<div className="relative bg-white rounded-sm shadow-xl max-w-3xl w-full">
						{/* Header */}
						<div className="flex items-center justify-between p-6 border-b border-gray-200">
							<div>
								<h2 className="text-xl font-glacial uppercase text-gray-900">
									Discover Lookup Keys
								</h2>
								<p className="text-sm font-montserrat-regular text-gray-600 mt-1">
									Scan Stripe for new subscription lookup keys
								</p>
							</div>
							<button
								onClick={onClose}
								disabled={isImporting}
								className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
								aria-label="Close modal">
								<FiX className="h-6 w-6" />
							</button>
						</div>

						{/* Content */}
						<div className="p-6">
							{isDiscovering ? (
								// Loading State
								<div className="text-center py-12">
									<FiLoader className="h-12 w-12 text-primary mx-auto mb-4 animate-spin" />
									<p className="text-lg font-montserrat-medium text-gray-900 mb-2">
										Discovering Lookup Keys
									</p>
									<p className="text-sm font-montserrat-regular text-gray-600">
										Scanning Stripe for available lookup keys...
									</p>
								</div>
							) : error ? (
								// Error State
								<div className="text-center py-12">
									<FiAlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
									<p className="text-lg font-montserrat-medium text-gray-900 mb-2">
										Discovery Failed
									</p>
									<p className="text-sm font-montserrat-regular text-gray-600 mb-6">
										{error}
									</p>
									<button
										onClick={handleDiscover}
										className="bg-clientBlack text-white px-6 py-2 rounded font-montserrat-medium text-sm hover:bg-gray-800 transition-colors">
										Try Again
									</button>
								</div>
							) : discoveredKeys.length === 0 ? (
								// No New Keys State
								<div className="text-center py-12">
									<FiCheck className="h-12 w-12 text-green-500 mx-auto mb-4" />
									<p className="text-lg font-montserrat-medium text-gray-900 mb-2">
										All Synced Up
									</p>
									<p className="text-sm font-montserrat-regular text-gray-600 mb-6">
										No new lookup keys found in Stripe. All available keys are already in the system.
									</p>
									<button
										onClick={onClose}
										className="bg-clientBlack text-white px-6 py-2 rounded font-montserrat-medium text-sm hover:bg-gray-800 transition-colors">
										Close
									</button>
								</div>
							) : importResults ? (
								// Import Success State
								<div className="text-center py-12">
									<FiCheck className="h-12 w-12 text-green-500 mx-auto mb-4" />
									<p className="text-lg font-montserrat-medium text-gray-900 mb-2">
										Import Complete
									</p>
									<p className="text-sm font-montserrat-regular text-gray-600 mb-2">
										Successfully imported {importResults.summary.successful} lookup key{importResults.summary.successful !== 1 ? 's' : ''}
									</p>
									{importResults.summary.failed > 0 && (
										<p className="text-xs font-montserrat-regular text-gray-500">
											Skipped {importResults.summary.failed} key{importResults.summary.failed !== 1 ? 's' : ''} (already existed)
										</p>
									)}
								</div>
							) : (
								// Selection State
								<div>
									{/* Select All */}
									<div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
										<p className="text-sm font-montserrat-regular text-gray-700">
											Found {discoveredKeys.length} new lookup key{discoveredKeys.length !== 1 ? 's' : ''} in Stripe
										</p>
										<button
											onClick={handleSelectAll}
											className="text-sm font-montserrat-medium text-primary hover:text-primary/80 transition-colors">
											{selectedKeys.length === discoveredKeys.length ? 'Deselect All' : 'Select All'}
										</button>
									</div>

									{/* Keys List */}
									<div className="space-y-3 max-h-96 overflow-y-auto">
										{discoveredKeys.map((key) => {
											const isSelected = selectedKeys.includes(key.lookupKey);

											return (
												<div
													key={key.lookupKey}
													onClick={() => handleToggleKey(key.lookupKey)}
													className={`p-4 border rounded cursor-pointer transition-all ${
														isSelected
															? 'border-primary bg-primary/5'
															: 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
													}`}>
													<div className="flex items-start gap-3">
														{/* Checkbox */}
														<div className="flex-shrink-0 mt-1">
															<div
																className={`h-5 w-5 rounded border-2 flex items-center justify-center transition-colors ${
																	isSelected
																		? 'border-primary bg-primary'
																		: 'border-gray-300 bg-white'
																}`}>
																{isSelected && (
																	<FiCheck className="h-3 w-3 text-white" />
																)}
															</div>
														</div>

														{/* Key Info */}
														<div className="flex-1 min-w-0">
															<code className="text-sm font-mono text-gray-800 block mb-1">
																{key.lookupKey}
															</code>
															{key.productName && (
																<p className="text-xs font-montserrat-regular text-gray-600">
																	Product: {key.productName}
																</p>
															)}
															{key.priceAmount && (
																<p className="text-xs font-montserrat-regular text-gray-500">
																	${(key.priceAmount / 100).toFixed(2)} / {key.priceInterval}
																</p>
															)}
														</div>
													</div>
												</div>
											);
										})}
									</div>

									{/* Selected Count */}
									{selectedKeys.length > 0 && (
										<div className="mt-4 p-3 bg-primary/10 rounded">
											<p className="text-sm font-montserrat-medium text-gray-900">
												{selectedKeys.length} key{selectedKeys.length !== 1 ? 's' : ''} selected for import
											</p>
										</div>
									)}
								</div>
							)}
						</div>

						{/* Footer */}
						{!isDiscovering && !error && discoveredKeys.length > 0 && !importResults && (
							<div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
								<button
									onClick={onClose}
									disabled={isImporting}
									className="bg-gray-200 text-gray-700 px-6 py-2 rounded font-montserrat-medium text-sm hover:bg-gray-300 transition-colors disabled:opacity-50">
									Cancel
								</button>
								<button
									onClick={handleImport}
									disabled={isImporting || selectedKeys.length === 0}
									className="bg-primary text-white px-6 py-2 rounded font-montserrat-medium text-sm hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
									{isImporting && <FiLoader className="h-4 w-4 animate-spin" />}
									{isImporting ? 'Importing...' : `Import ${selectedKeys.length > 0 ? `(${selectedKeys.length})` : ''}`}
								</button>
							</div>
						)}
					</div>
				</div>
			</div>
		</>
	);
};

DiscoveryModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onComplete: PropTypes.func.isRequired,
	token: PropTypes.string.isRequired,
	existingKeys: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number,
			lookupKey: PropTypes.string,
		})
	).isRequired,
};

export default DiscoveryModal;
